import { restRequest } from "./restClient"
import type { RouteHandler } from "./routes"

// Board channels over REST (proto/boards.proto, google.api.http annotations) — the browser-mode twin of
// src-tauri/src/commands/boards.rs. proto3 JSON leaves default values out (false, "", empty lists), sends enums
// by name and int64 as strings, so every response is normalized to the exact shape the Tauri DTOs have
// (app/types/boards.ts) before the store sees it.

type Args = Record<string, unknown>
type Raw = Record<string, unknown>

const BOARD_PROVIDER_NAMES = ["BOARD_PROVIDER_UNSPECIFIED", "YOUTRACK", "JIRA", "GITHUB", "GITLAB", "TRELLO"]

function payload(args: Args): Args {
  return (args.payload as Args | undefined) ?? {}
}

function str(value: unknown): string {
  return value == null ? "" : String(value)
}

function num(value: unknown): number {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

function list(value: unknown): Raw[] {
  return Array.isArray(value) ? (value as Raw[]) : []
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : []
}

function provider(value: unknown): number {
  if (typeof value === "number") return value
  const index = BOARD_PROVIDER_NAMES.indexOf(str(value))
  return index >= 0 ? index : num(value)
}

function externalUser(value: unknown) {
  if (!value || typeof value !== "object") return null
  const u = value as Raw
  return { id: str(u.id), login: str(u.login), name: str(u.name), avatar_url: str(u.avatar_url) }
}

function probeResult(value: unknown) {
  const r = (value ?? {}) as Raw
  return {
    succeeded: !!r.succeeded,
    steps: list(r.steps).map((s) => ({
      id: str(s.id),
      status: str(s.status),
      http_status: num(s.http_status),
      message: str(s.message),
    })),
    base_url: str(r.base_url),
    server_version: str(r.server_version),
    account: externalUser(r.account),
    error: str(r.error),
  }
}

function sprint(value: Raw) {
  return { id: str(value.id), name: str(value.name), archived: !!value.archived }
}

function mapping(value: unknown) {
  const m = (value ?? {}) as Raw
  return {
    column_field: str(m.column_field),
    assignee_field: str(m.assignee_field),
    priority_field: str(m.priority_field),
    stage_in_progress: str(m.stage_in_progress),
    stage_review: str(m.stage_review),
    stage_done: str(m.stage_done),
  }
}

function board(value: unknown) {
  const b = (value ?? {}) as Raw
  return {
    channel_id: str(b.channel_id),
    connected: !!b.connected,
    provider: provider(b.provider),
    base_url: str(b.base_url),
    external_board_id: str(b.external_board_id),
    name: str(b.name),
    url: str(b.url),
    columns: list(b.columns).map((c) => ({
      id: str(c.id),
      name: str(c.name),
      is_resolved: !!c.is_resolved,
      wip_limit_max: num(c.wip_limit_max),
    })),
    sprints: list(b.sprints).map(sprint),
    sprint_id: str(b.sprint_id),
    current_sprint_id: str(b.current_sprint_id),
    mapping: mapping(b.mapping),
    mapping_gaps: list(b.mapping_gaps).map((g) => ({ field: str(g.field), message: str(g.message) })),
    fields: list(b.fields).map((f) => ({
      id: str(f.id),
      name: str(f.name),
      kind: str(f.kind),
      options: list(f.options).map((o) => ({ id: str(o.id), name: str(o.name) })),
      required: f.required === true,
    })),
    connected_by_user_id: str(b.connected_by_user_id),
    connected_account: externalUser(b.connected_account),
    has_own_account: !!b.has_own_account,
    can_manage: !!b.can_manage,
    can_edit: !!b.can_edit,
    error: str(b.error),
    can_create: !!b.can_create,
    can_edit_tasks: !!b.can_edit_tasks,
    can_assign: !!b.can_assign,
    can_comment: !!b.can_comment,
    can_use_agents: !!b.can_use_agents,
    needs_account: !!b.needs_account,
  }
}

function card(value: unknown) {
  const c = (value ?? {}) as Raw
  return {
    id: str(c.id),
    key: str(c.key),
    title: str(c.title),
    column_id: str(c.column_id),
    priority: str(c.priority),
    priority_color: str(c.priority_color),
    tags: strings(c.tags),
    assignees: list(c.assignees).map((a) => ({
      kind: str(a.kind),
      external: externalUser(a.external),
      iopta_id: str(a.iopta_id),
      is_bot: !!a.is_bot,
      is_agent: !!a.is_agent,
    })),
    url: str(c.url),
    resolved: !!c.resolved,
    updated_at: num(c.updated_at),
  }
}

function entry(value: Raw) {
  return { id: str(value.id), author: externalUser(value.author), text: str(value.text), created_at: num(value.created_at) }
}

function account(value: unknown) {
  const a = (value ?? {}) as Raw
  return {
    provider: provider(a.provider),
    base_url: str(a.base_url),
    account: externalUser(a.account),
    linked_at: num(a.linked_at),
  }
}

function enc(value: unknown): string {
  return encodeURIComponent(str(value))
}

export const boardRoutes: Record<string, RouteHandler> = {
  probe_board_server: async (a) =>
    probeResult(await restRequest("/v1/boards/probe", { method: "POST", body: payload(a) })),
  verify_board_token: async (a) =>
    probeResult(await restRequest("/v1/boards/verify-token", { method: "POST", body: payload(a) })),
  list_external_boards: async (a) => {
    const res = await restRequest<Raw>("/v1/boards/external", { method: "POST", body: payload(a) })
    return list(res.boards).map((b) => ({
      id: str(b.id),
      name: str(b.name),
      projects: strings(b.projects),
      has_sprints: !!b.has_sprints,
      sprints: list(b.sprints).map(sprint),
      current_sprint_id: str(b.current_sprint_id),
    }))
  },
  connect_board: async (a) => {
    const p = payload(a)
    return board(await restRequest(`/v1/boards/${enc(p.channel_id)}/connect`, { method: "POST", body: p }))
  },
  update_board_mapping: async (a) => {
    const p = payload(a)
    const body: Args = { channel_id: p.channel_id }
    if (p.mapping) body.mapping = p.mapping
    if (p.sprint_id !== null && p.sprint_id !== undefined) body.sprint_id = p.sprint_id
    return board(await restRequest(`/v1/boards/${enc(p.channel_id)}/mapping`, { method: "POST", body }))
  },
  disconnect_board: async (a) => {
    await restRequest(`/v1/boards/${enc(a.channelId)}/connect`, { method: "DELETE" })
  },

  get_board: async (a) => board(await restRequest(`/v1/boards/${enc(a.channelId)}`)),
  list_board_cards: async (a) => {
    const p = payload(a)
    // Built by hand: `card_ids` is repeated (one `cardIds=` per id), which `restRequest`'s `query` can't express.
    const params: string[] = []
    if (p.query) params.push(`query=${enc(p.query)}`)
    if (p.sprint_id) params.push(`sprintId=${enc(p.sprint_id)}`)
    for (const id of strings(p.card_ids)) params.push(`cardIds=${enc(id)}`)
    const qs = params.length ? `?${params.join("&")}` : ""
    const res = await restRequest<Raw>(`/v1/boards/${enc(p.channel_id)}/cards${qs}`)
    return list(res.cards).map(card)
  },
  get_board_card: async (a) => {
    const d = await restRequest<Raw>(`/v1/boards/${enc(a.channelId)}/cards/${enc(a.cardId)}`)
    return {
      card: d.card ? card(d.card) : null,
      description: str(d.description),
      fields: list(d.fields).map((f) => ({ field_id: str(f.field_id), name: str(f.name), value: str(f.value) })),
      comments: list(d.comments).map(entry),
      activity: list(d.activity).map(entry),
      sprint: str(d.sprint),
    }
  },
  watch_board: async (a) => {
    await restRequest(`/v1/boards/${enc(a.channelId)}/watch`, { method: "POST", body: {} })
  },

  create_board_card: async (a) => {
    const p = payload(a)
    return card(await restRequest(`/v1/boards/${enc(p.channel_id)}/cards`, { method: "POST", body: p }))
  },
  // Caveat: `fields` keys are tracker field names, and the request body's snake->camel conversion would also
  // rewrite a name containing `_` — fine for YouTrack's usual «Priority»/«Stage», wrong for an exotic one.
  update_board_card: async (a) => {
    const p = payload(a)
    return card(await restRequest(`/v1/boards/${enc(p.channel_id)}/cards/${enc(p.card_id)}`, { method: "POST", body: p }))
  },
  move_board_card: async (a) => {
    const p = payload(a)
    return card(await restRequest(`/v1/boards/${enc(p.channel_id)}/cards/${enc(p.card_id)}:move`, { method: "POST", body: p }))
  },
  set_board_card_assignees: async (a) => {
    const p = payload(a)
    return card(await restRequest(`/v1/boards/${enc(p.channel_id)}/cards/${enc(p.card_id)}:assign`, { method: "POST", body: p }))
  },
  comment_board_card: async (a) =>
    entry(await restRequest<Raw>(`/v1/boards/${enc(a.channelId)}/cards/${enc(a.cardId)}/comments`, {
      method: "POST",
      body: { channel_id: a.channelId, card_id: a.cardId, text: a.text },
    })),

  list_board_assignees: async (a) => {
    const res = await restRequest<Raw>(`/v1/boards/${enc(a.channelId)}/assignees`, { query: { query: a.query || undefined } })
    return list(res.options).map((o) => ({
      source: str(o.source),
      external: externalUser(o.external),
      iopta_id: str(o.iopta_id),
      name: str(o.name),
      avatar_url: str(o.avatar_url),
      is_agent: !!o.is_agent,
      has_external_account: !!o.has_external_account,
    }))
  },
  link_board_account: async (a) =>
    account(await restRequest("/v1/boards/accounts", {
      method: "POST",
      body: { provider: a.provider, base_url: a.baseUrl, token: a.token },
    })),
  unlink_board_account: async (a) => {
    await restRequest("/v1/boards/accounts:unlink", { method: "POST", body: { provider: a.provider, base_url: a.baseUrl } })
  },
  list_my_board_accounts: async () => {
    const res = await restRequest<Raw>("/v1/boards/accounts")
    return list(res.accounts).map(account)
  },
}
